import { Module } from "components/Admin/Organizations/types/Module.type";
import {
    AISummaryResult,
    ISignalsChartResponse,
    ISignalsDrilldownResponse,
    ISignalsDriversDrilldownResponse,
    ISignalsTopicsDrilldownResponse,
    ISignalsDriversOnTheRiseResponse,
    ISignalsTopicsOnTheRiseResponse,
    ISignalsZoomResponse,
    ISignalsDashboard,
    SignalDataEndpoint,
    SignalsFilters,
    SignalsRequest,
    SignalsThemes,
    SignalsTopics,
    ISignalsTextCard,
    ISignalsMontageCard,
    SignalsMontageResponse,
    ISignalsSmallCard,
    IDashboardTenantVisibility,
    IDashboardUserPermission,
    SignalsEmailCadence,
    ExportableFieldsResponse,
    SignalsGroupByCatalogItem,
    SignalsOrderRuleCatalogItem,
    SignalsTableResponse,
} from "components/Signals/SignalsModels";
import type {
    PrimaryDriver,
    PrimaryTopic,
} from "stores/ApplicationFilters/ApplicationFiltersStore";
import BaseService, { ServiceError } from "./BaseService";
import { SignalReportDatum } from "models/Reporting/ReportDatum";
import { v4 as uuid } from "uuid";
import { ReportAccessType } from "models/Reporting/ReportAccessControls";
import {
    ISignalsChartDefinition,
    RenderedSignalsObject,
    SignalsObjectType,
} from "components/Signals/SignalsModels";
import { ApplicationFilters } from "stores/ApplicationFilters/ApplicationFiltersStore";
import { ApplicationFilterDomain } from "./ApplicationFiltersService";
import { FeedbackType } from "components/Signals/BaseObjects/ChartCard/ChartFeatureStores/AISummaryStore";
import { SmallCardRequestData } from "components/Signals/BaseObjects/SmallCard/SmallCardStore";
import type {
    DashboardLayoutResponse,
    LayoutItem,
} from "components/Signals/Dashboard/Layout/types";

export interface SelectedCardRequestPayload {
    id?: string;
    type: SignalsObjectType;
    info: any;
    label?: string;
}

export interface UpdateSignalsUniqueCardRequest {
    id: string;
    title?: string;
    subtitle?: string;
    objectFilters?: string | null;
}

export interface UpdateSignalsTableCardRequest {
    id: string;
    title?: string;
    subtitle?: string;
    objectFilters?: string | null;
    vizOptions?: string;
}

export interface UpdateSignalsSmallCardRequest {
    id: string;
    title?: string;
    objectFilters?: string | null;
}

export interface ImmediateDashboardEmailObjectOverride {
    objectFilters?: string | null;
    vizOptions?: string;
}

export type ImmediateDashboardEmailObjectOverridesById = Record<
    string,
    ImmediateDashboardEmailObjectOverride
>;

export interface ScheduleEmailRequest {
    dashboardId: string;
    cadence: SignalsEmailCadence;

    // for when we need to save a new application filter then pin it to dashboard
    name?: string;
    domain?: ApplicationFilterDomain;
    value?: ApplicationFilters;

    // for when we just need to pin the already saved filter
    filterIdToPin?: string;
}

export interface ScheduleEmailResponse {
    message: string;
    newSubscriptionId?: string;
    newFilterId?: string;
}

export class SignalsService extends BaseService {
    protected urlBase = "api/signals";

    private abortSignal?: AbortSignal;

    /**
     * Sets the abort signal that will be used to cancel all report and stat requests
     * that are currently active
     * @param abortSignal
     */
    public setAbortSignal(abortSignal?: AbortSignal) {
        this.abortSignal = abortSignal;
    }

    public async refreshAISummary(
        dataEndpoint: SignalDataEndpoint,
        chartState: SignalsRequest,
    ) {
        const params = new URLSearchParams();
        const { basePath, queryParams } = this.splitUrlParams(
            dataEndpoint,
            params,
        );

        const apiPath = `${basePath}/aisummary`;

        return this.post<AISummaryResult>(
            JSON.stringify(chartState),
            queryParams,
            apiPath,
        );
    }

    public async refreshAISummaryWithRowData(
        dataEndpoint: SignalDataEndpoint,
        data: SignalReportDatum[],
    ) {
        const params = new URLSearchParams();
        const { basePath, queryParams } = this.splitUrlParams(
            dataEndpoint,
            params,
        );

        const apiPath = `${basePath}/aisummary`;

        return this.post<AISummaryResult>(
            JSON.stringify({ data }),
            queryParams,
            apiPath,
            {},
            uuid(), // use a unique debounceKey so that dashboards can have multiples of the same chart
        );
    }

    public async aiSummaryFeedback(
        protectedDataBase64: string,
        feedbackType: FeedbackType,
        feedback: string | null = null,
    ) {
        const params = new URLSearchParams();
        const apiPath = "AISummaryFeedback";

        return this.post<void>(
            JSON.stringify({
                protectedDataBase64,
                feedbackType,
                feedback,
            }),
            params,
            apiPath,
        );
    }

    public async getChart(
        dataEndpoint: string,
        chartState: SignalsRequest,
        abortSignal: AbortSignal,
    ) {
        const params = new URLSearchParams();
        const { basePath, queryParams } = this.splitUrlParams(
            dataEndpoint,
            params,
        );

        let apiPath = basePath;
        return this.post<ISignalsChartResponse>(
            JSON.stringify(chartState),
            queryParams,
            apiPath,
            undefined,
            undefined,
            abortSignal ?? this.abortSignal,
        );
    }

    public async getGroupByCatalog() {
        // Group-by catalog drives the grouping dropdown and backend alias mapping.
        return this.get<SignalsGroupByCatalogItem[]>(
            new URLSearchParams(),
            "GetGroupByCatalog",
        );
    }

    public async getOrderRuleCatalog() {
        // Order-rule catalog maps group-by dimensions to deterministic frontend ordering.
        return this.get<SignalsOrderRuleCatalogItem[]>(
            new URLSearchParams(),
            "GetOrderRuleCatalog",
        );
    }

    // Reorder a per-chart order rule's values (internal users only; reorder-only on the backend).
    // Returns the persisted order on success.
    public async updateOrderRuleValues(orderRuleId: string, values: string[]) {
        return this.put<string[]>(
            new URLSearchParams(),
            "UpdateOrderRuleValues",
            JSON.stringify({ orderRuleId, values }),
        );
    }

    public async getChartExport(
        dataEndpoint: string,
        chartState: SignalsRequest,
        selectedColumns?: string[],
    ) {
        const params = new URLSearchParams();
        const { basePath, queryParams } = this.splitUrlParams(
            dataEndpoint,
            params,
        );

        const apiPath = `${basePath}/export`;

        const response: Response = await this.post(
            JSON.stringify({ ...chartState, columns: selectedColumns }),
            queryParams,
            apiPath,
            undefined,
            undefined,
            undefined,
            true,
        );

        return response.blob();
    }

    public async getDrillDownForChart(
        dataEndpoint: SignalDataEndpoint,
        chartState: SignalsRequest,
        abortSignal?: AbortSignal,
    ) {
        const params = new URLSearchParams();
        const { basePath, queryParams } = this.splitUrlParams(
            dataEndpoint,
            params,
        );

        const apiPath = `${basePath}/drilldown`;
        return this.post<ISignalsDrilldownResponse>(
            JSON.stringify(chartState),
            queryParams,
            apiPath,
            undefined,
            undefined,
            abortSignal,
        );
    }

    // Fetches one zoom level of a zoomable chart (e.g. TreeMap). The base
    // chart endpoint returns level 0; this attached `${dataEndpoint}/zoom`
    // endpoint accepts the full path from root and returns the children of the
    // deepest entry. Response echoes `zoomPath` so out-of-order responses can
    // be discarded by the caller.
    public async getChartZoom(
        dataEndpoint: SignalDataEndpoint,
        chartState: SignalsRequest & { zoomPath: string[] },
        abortSignal?: AbortSignal,
    ) {
        const params = new URLSearchParams();
        const { basePath, queryParams } = this.splitUrlParams(
            dataEndpoint,
            params,
        );

        const apiPath = `${basePath}/zoom`;
        return this.post<ISignalsZoomResponse>(
            JSON.stringify(chartState),
            queryParams,
            apiPath,
            undefined,
            uuid(),
            abortSignal,
        );
    }

    public async getTopicDrillDownForChart(
        dataEndpoint: SignalDataEndpoint,
        chartState: SignalsRequest,
        abortSignal?: AbortSignal,
    ) {
        const params = new URLSearchParams();
        const { basePath, queryParams } = this.splitUrlParams(
            dataEndpoint,
            params,
        );

        const apiPath = `${basePath}/topicdrilldown`;

        return this.post<ISignalsTopicsDrilldownResponse>(
            JSON.stringify(chartState),
            queryParams,
            apiPath,
            undefined,
            undefined,
            abortSignal,
        );
    }

    /**
     * Breaks a Theme Data Point into its contributing Primary Drivers. The Topic counterpart is
     * {@link getTopicDrillDownForChart}.
     */
    public async getDriverDrillDownForChart(
        dataEndpoint: SignalDataEndpoint,
        chartState: SignalsRequest,
        abortSignal?: AbortSignal,
    ) {
        const params = new URLSearchParams();
        const { basePath, queryParams } = this.splitUrlParams(
            dataEndpoint,
            params,
        );

        const apiPath = `${basePath}/driverdrilldown`;

        return this.post<ISignalsDriversDrilldownResponse>(
            JSON.stringify(chartState),
            queryParams,
            apiPath,
            undefined,
            undefined,
            abortSignal,
        );
    }

    public async getConversationsForDriver(chartState: SignalsRequest) {
        const params = new URLSearchParams();
        const apiPath = "Themes/conversationdrilldown";

        return this.post<ISignalsDrilldownResponse>(
            JSON.stringify(chartState),
            params,
            apiPath,
            undefined,
            undefined,
            this.abortSignal,
        );
    }

    public async getConversationsForTopic(chartState: SignalsRequest) {
        const params = new URLSearchParams();
        const apiPath = "Topics/conversationdrilldown";

        return this.post<ISignalsDrilldownResponse>(
            JSON.stringify(chartState),
            params,
            apiPath,
            undefined,
            undefined,
            this.abortSignal,
        );
    }

    public async getSmallCardData(
        request: SignalsFilters,
        dataEndpoint: string,
        abortSignal?: AbortSignal,
    ) {
        // If true, we add a query parameter to prevent a second call from being ignored

        const params = new URLSearchParams();

        return this.post<SmallCardRequestData>(
            JSON.stringify(request),
            params,
            dataEndpoint,
            undefined,
            undefined,
            abortSignal ?? this.abortSignal,
        );
    }

    public async getLicensedModulesWithScoredQuestions() {
        const apiPath = "GetLicensedModulesWithScoredQuestions";
        return this.get<Module[]>(new URLSearchParams({}), apiPath);
    }

    public async getPrimaryTopics() {
        const apiPath = "primary-topics";
        return this.get<PrimaryTopic[]>(new URLSearchParams(), apiPath);
    }

    public async getPrimaryDrivers() {
        const apiPath = "primary-drivers";
        return this.get<PrimaryDriver[]>(new URLSearchParams(), apiPath);
    }

    // Is actually themes
    public async getTopics() {
        const apiPath = "topics";
        return this.get<SignalsTopics[]>(new URLSearchParams(), apiPath);
    }

    /**
     * Undated Primary Driver Theme catalog, backing the "Themes" group-by value list.
     * {@link getAllDriverThemes} is the date-ranged, prevalence-estimated equivalent used by the
     * Application Filter picker.
     */
    public async getThemes() {
        const apiPath = "themes";
        return this.get<SignalsThemes[]>(new URLSearchParams(), apiPath);
    }

    /**
     * Legacy Theme catalog, read through the Fast Reporting Topic path. Backs the deprecated
     * "topics" Application Filter. The Primary Driver equivalent is
     * {@link getAllDriverThemes}.
     */
    public async getAllThemes(startDate: string, endDate: string) {
        const apiPath = "topics/trending";
        return this.get<SignalsTopics[]>(
            new URLSearchParams({
                startDate,
                endDate,
            }),
            apiPath,
        );
    }

    /**
     * Theme catalog read through the Primary Driver. Backs the "themes" Application Filter and
     * the Theme Cards. Requires Drivers/View.
     */
    public async getAllDriverThemes(startDate: string, endDate: string) {
        const apiPath = "themes/trending";
        return this.get<SignalsThemes[]>(
            new URLSearchParams({
                startDate,
                endDate,
            }),
            apiPath,
        );
    }

    public async getTableData(
        chartState: SignalsRequest,
        dataEndpoint: string,
        pageNumber: number,
    ) {
        const request = { ...chartState, pageNumber };
        const params = new URLSearchParams();
        return this.post<SignalsTableResponse>(
            JSON.stringify(request),
            params,
            dataEndpoint,
            undefined,
            undefined,
            this.abortSignal,
        );
    }

    public async getTopicsOnTheRise(chartState: SignalsRequest) {
        const apiPath = "TopicsOnTheRise";

        const params = new URLSearchParams();
        return this.post<ISignalsTopicsOnTheRiseResponse>(
            JSON.stringify(chartState),
            params,
            apiPath,
            undefined,
            undefined,
            this.abortSignal,
        );
    }

    public async getDriversOnTheRise(chartState: SignalsRequest) {
        const apiPath = "DriversOnTheRise";

        const params = new URLSearchParams();
        return this.post<ISignalsDriversOnTheRiseResponse>(
            JSON.stringify(chartState),
            params,
            apiPath,
            undefined,
            undefined,
            this.abortSignal,
        );
    }

    public async getDashboards(): Promise<ISignalsDashboard[]> {
        const apiPath = "GetDashboards";
        return this.get<ISignalsDashboard[]>(new URLSearchParams({}), apiPath);
    }

    public async getDashboard(id: string): Promise<ISignalsDashboard | null> {
        const apiPath = "GetDashboard";
        try {
            return await this.get<ISignalsDashboard>(
                new URLSearchParams({ id }),
                apiPath,
            );
        } catch (error) {
            if (
                error instanceof ServiceError &&
                error.response.status === 404
            ) {
                return null;
            }
            throw error;
        }
    }

    public async createDashboard(request: {
        title: string;
        description?: string;
        logoName?: string;
    }) {
        const apiPath = "CreateDashboard";
        return this.post<ISignalsDashboard>(
            JSON.stringify(request),
            new URLSearchParams(),
            apiPath,
        );
    }

    public async duplicateDashboard({
        id,
        title,
    }: {
        id: string;
        title: string;
    }): Promise<ISignalsDashboard> {
        const apiPath = "DuplicateSignalsDashboard";
        const params = new URLSearchParams();

        return this.post<ISignalsDashboard>(
            JSON.stringify({ id, title }),
            params,
            apiPath,
        );
    }

    public async updateDashboard(request: {
        id: string;
        title?: string;
        description?: string;
        logoName?: string;
        pinnedApplicationFilterId?: string;
    }) {
        const apiPath = "UpdateDashboard";
        return this.put<ISignalsDashboard>(
            new URLSearchParams(),
            apiPath,
            JSON.stringify(request),
        );
    }
    public async deleteDashboard(request: { id: string }) {
        const apiPath = "DeleteDashboard";
        return this.delete(new URLSearchParams(request), apiPath);
    }

    public async updateSignalsChart(request: {
        id: string;
        dataEndpoint?: string;
        title?: string;
        subtitle?: string;
        xTitle?: string;
        yTitle?: string;
        vizOptions?: string;
        objectFilters?: string | null;
    }) {
        const apiPath = "UpdateSignalsChart";

        const body: string = JSON.stringify(request);
        return this.put<ISignalsChartDefinition>(
            new URLSearchParams(),
            apiPath,
            body,
        );
    }
    public async deleteSignalsChart(request: { id: string }): Promise<void> {
        return this.deleteSignalsObject("DeleteSignalsChart", request.id);
    }

    public async addSignalsChartsToDashboard(
        dashboardId: string,
        signalsCharts: ISignalsChartDefinition[],
    ) {
        const apiPath = "AddSignalsChartToDashboard";

        // need to remove extra fields because stringify is failing for larger dataStructures when adding many vizDefs
        // We need to make sure the right definition details are added.
        const formattedDefs = signalsCharts.map((v) => {
            return {
                dataEndpoint: v.dataEndpoint,
                title: v.title,
                subtitle: v.subtitle,
                xTitle: v.xTitle,
                yTitle: v.yTitle,
                vizOptions: v.vizOptions,
                objectFilters: v.objectFilters,
                typeOfChart: v.typeOfChart,
                typeOfDataSelectedInChart: v.typeOfDataSelectedInChart,
                availableRenderModes: v.availableRenderModes,
                defaultRenderMode: v.defaultRenderMode,
                isPaginated: v.isPaginated,
                compareToPreviousModeIsAvailable:
                    v.compareToPreviousModeIsAvailable,
                isTemporal: v.isTemporal,
                showDataGrid: v.showDataGrid,
                showSpotlight: v.showSpotlight,
                showAnalyzeWithKBo: v.showAnalyzeWithKBo,
                orderRuleId: v.orderRuleId,
            };
        });

        const body: string = JSON.stringify(formattedDefs);

        return this.post<ISignalsChartDefinition[]>(
            body,
            new URLSearchParams({ dashboardId }),
            apiPath,
        );
    }

    public async addSignalsTextCardToDashboard(
        dashboardId: string,
        signalsTextCards: ISignalsTextCard[],
    ) {
        const apiPath = "AddSignalsTextCardToDashboard";

        const body: string = JSON.stringify(signalsTextCards);

        return this.post<ISignalsTextCard[]>(
            body,
            new URLSearchParams({ dashboardId }),
            apiPath,
        );
    }

    public async addSignalsMontageCardToDashboard(
        dashboardId: string,
        signalsMontageCards: ISignalsMontageCard[],
    ) {
        const apiPath = "AddSignalsMontageCardToDashboard";

        const body: string = JSON.stringify(signalsMontageCards);

        return this.post<ISignalsMontageCard[]>(
            body,
            new URLSearchParams({ dashboardId }),
            apiPath,
        );
    }

    public async addSignalsSmallCardsToDashboard(
        dashboardId: string,
        signalsSmallCards: ISignalsSmallCard[],
    ) {
        const apiPath = "AddSignalsSmallCardToDashboard";

        const body: string = JSON.stringify(signalsSmallCards);

        return this.post<ISignalsSmallCard[]>(
            body,
            new URLSearchParams({ dashboardId }),
            apiPath,
        );
    }

    public async addSelectedCardsToDashboard(
        dashboardId: string,
        selectedCards: SelectedCardRequestPayload[],
    ) {
        const apiPath = "AddSelectedCardsToDashboard";

        const body: string = JSON.stringify({ selectedCards: selectedCards });

        return this.post<RenderedSignalsObject[]>(
            body,
            new URLSearchParams({ dashboardId }),
            apiPath,
        );
    }

    public async updateSignalsTextCard(id: string, cardText: string) {
        const apiPath = "UpdateSignalsTextCard";

        const body: string = JSON.stringify({
            id,
            cardText,
        });

        return this.put<ISignalsTextCard>(
            new URLSearchParams({}),
            apiPath,
            body,
        );
    }

    public async getSignalsMontage(audioId: string, abortSignal?: AbortSignal) {
        const apiPath = "SignalMontage";

        const body: string = JSON.stringify({
            audioId,
        });

        return this.post<SignalsMontageResponse>(
            body,
            new URLSearchParams({}),
            apiPath,
            undefined,
            undefined,
            abortSignal,
        );
    }

    public async deleteSignalsTextCard({ id }: { id: string }): Promise<void> {
        return this.deleteSignalsObject("DeleteSignalsTextCard", id);
    }

    public async deleteSignalsMontageCard({
        id,
    }: {
        id: string;
    }): Promise<void> {
        return this.deleteSignalsObject("DeleteSignalsMontageCard", id);
    }

    public async deleteSignalsSmallCard({ id }: { id: string }): Promise<void> {
        return this.deleteSignalsObject("DeleteSignalsSmallCard", id);
    }

    public async deleteSignalsTableCard({ id }: { id: string }): Promise<void> {
        return this.deleteSignalsObject("DeleteSignalsTableCard", id);
    }

    public async deleteSignalsUniqueCard({
        id,
    }: {
        id: string;
    }): Promise<void> {
        return this.deleteSignalsObject("DeleteSignalsUniqueCard", id);
    }

    /** Deletes a card and removes it from its dashboard's layout. */
    private async deleteSignalsObject(apiPath: string, id: string) {
        await this.delete(new URLSearchParams({ id }), apiPath);
    }

    /**
     * Replaces a dashboard's layout. Always succeeds: the server drops cards
     * deleted since the page loaded and appends cards added since, and says
     * which in the response.
     */
    public async updateDashboardLayout(request: {
            dashboardId: string;
            schemaVersion: number;
            columns: number;
        items: LayoutItem[];
    }): Promise<DashboardLayoutResponse> {
        return this.put<DashboardLayoutResponse>(
            new URLSearchParams(),
            "UpdateDashboardLayout",
            JSON.stringify(request),
            { returnJson: true },
        );
    }

    public async updateSignalsMontageCard(id: string, title?: string) {
        const apiPath = "UpdateSignalsMontageCard";

        const body: string = JSON.stringify({
            id,
            title,
        });

        return this.put<ISignalsTextCard>(
            new URLSearchParams({}),
            apiPath,
            body,
        );
    }

    public async updateSignalsUniqueCard(
        request: UpdateSignalsUniqueCardRequest,
    ) {
        const apiPath = "UpdateSignalsUniqueCard";

        const body: string = JSON.stringify(request);

        return this.put(new URLSearchParams({}), apiPath, body);
    }

    public async updateSignalsTableCard(
        request: UpdateSignalsTableCardRequest,
    ) {
        const apiPath = "UpdateSignalsTableCard";

        const body: string = JSON.stringify(request);

        return this.put(new URLSearchParams({}), apiPath, body);
    }

    public async updateSignalsSmallCard(
        request: UpdateSignalsSmallCardRequest,
    ) {
        const apiPath = "UpdateSignalsSmallCard";

        const body: string = JSON.stringify(request);

        return this.put(new URLSearchParams({}), apiPath, body);
    }

    public async saveLogo(
        uploadedFile: File,
        dashboardId: string,
    ): Promise<string> {
        const formData = new FormData();
        formData.append("uploadedFile", uploadedFile);

        return await this.post(
            formData,
            new URLSearchParams({
                logoName: uploadedFile.name,
                signalsDashboardId: dashboardId,
            }),
            "SaveLogoFile",
        );
    }

    public async deleteLogo(fileName: string, dashboardId: string) {
        await this.delete(
            new URLSearchParams({ fileName, dashboardId }),
            "DeleteLogo",
        );
    }

    public async getLogos() {
        return await this.get(new URLSearchParams({}), "GetLogoList");
    }

    /* Sharing related methods */
    public async updateDashboardVisibility(
        id: string,
        tenantVisibility: IDashboardTenantVisibility,
    ) {
        const apiPath = "UpdateDashboardTenantVisibility";

        const body: string = JSON.stringify({
            id,
            tenantVisibility,
        });

        return this.patch<ISignalsDashboard>(
            body,
            new URLSearchParams({}),
            apiPath,
        );
    }

    public async getDashboardUserPermissions(
        id: string,
    ): Promise<IDashboardUserPermission[]> {
        const apiPath = "GetDashboardUserPermissions";

        return this.get<IDashboardUserPermission[]>(
            new URLSearchParams({ id }),
            apiPath,
        );
    }

    public async updateDashboardUserPermissions(
        id: string,
        userIds: string[],
        accessType: ReportAccessType,
    ): Promise<IDashboardUserPermission[]> {
        const apiPath = "UpdateDashboardUserPermissions";

        const body: string = JSON.stringify({
            id,
            userIds,
            accessType,
        });

        return this.patch<IDashboardUserPermission[]>(
            body,
            new URLSearchParams({}),
            apiPath,
        );
    }

    public async deleteDashboardUserPermission(
        dashboardId: string,
        userId: string,
    ): Promise<IDashboardUserPermission> {
        const apiPath = "DeleteDashboardUserPermission";
        const body = JSON.stringify({
            dashboardId,
            userId,
        });
        const res = await this.delete(new URLSearchParams({}), apiPath, body);
        return res.body as unknown as IDashboardUserPermission;
    }

    public async sendImmediateDashboardEmail(
        dashboardId: string,
        filters: ApplicationFilters,
        objectOverridesById?: ImmediateDashboardEmailObjectOverridesById,
    ): Promise<{ message: string }> {
        return this.post(
            JSON.stringify({ dashboardId, filters, objectOverridesById }),
            new URLSearchParams(),
            "SendImmediateEmail",
        );
    }

    public async scheduleDashboardEmail(
        request: ScheduleEmailRequest,
    ): Promise<ScheduleEmailResponse> {
        const { dashboardId, cadence, name, value, domain, filterIdToPin } =
            request;

        return this.post(
            JSON.stringify({
                dashboardId,
                cadence,
                name,
                value: JSON.stringify(value),
                domain,
                filterIdToPin,
            }),
            new URLSearchParams(),
            "ScheduleDashboardEmail",
        );
    }

    public async unsubscribeDashboardEmail(
        signalsDashboardSubscriptionId: string,
    ) {
        const params = new URLSearchParams({ signalsDashboardSubscriptionId });
        return this.delete(params, "UnsubscribeSignalsDashboard");
    }

    public async getBaseObjectData({
        dataEndpoint,
        signalsRequest,
        abortSignal,
    }: {
        dataEndpoint: SignalDataEndpoint;
        signalsRequest: SignalsRequest;
        abortSignal?: AbortSignal;
    }) {
        const params = new URLSearchParams();
        const { basePath, queryParams } = this.splitUrlParams(
            dataEndpoint,
            params,
        );

        let apiPath = basePath;
        return this.post<any>(
            JSON.stringify(signalsRequest),
            queryParams,
            apiPath,
            undefined,
            undefined,
            abortSignal,
        );
    }

    public async getExportableFields(): Promise<ExportableFieldsResponse> {
        return this.get(new URLSearchParams(), "ExportableFields");
    }

    public async getChartExportableFields(
        dataEndpoint: SignalDataEndpoint,
    ): Promise<ExportableFieldsResponse> {
        const params = new URLSearchParams();
        const { basePath } = this.splitUrlParams(dataEndpoint, params);
        params.set("dataEndpoint", basePath);
        return this.get(params, "ExportableFields/Chart");
    }

    public async getExtractedConversationExportableFields(
        classifierId: string,
    ): Promise<ExportableFieldsResponse> {
        const params = new URLSearchParams({
            extendedMetadataClassifierId: classifierId,
        });
        return this.get(params, "ExportableFields/ExtractedConversationData");
    }
}
